import { PaymentMethodType } from '@bofrak-backend/shared';
import { tShared } from '../i18n';

export const getErrorMessage = (error: unknown): string => {
  // Nullish
  if (error == null) return tShared('An unexpected error occurred');

  // Plain string thrown
  if (typeof error === 'string') return error;

  // Plain number/boolean thrown
  if (typeof error !== 'object') return String(error);

  const err = error as Record<string, any>;

  // Axios-style: error.response.data
  const data = err.response?.data;
  if (data) {
    const msg = extractMessage(
      data.message ?? data.error ?? data.detail ?? data.errors ?? data,
    );
    if (msg) return msg;
  }

  // Fetch Response object (status + statusText)
  if (typeof err.status === 'number' && typeof err.statusText === 'string') {
    return `${err.status}: ${err.statusText}`;
  }

  // Standard Error / custom shapes
  const msg = extractMessage(
    err.message ?? err.error ?? err.detail ?? err.errors,
  );
  if (msg) return msg;

  // Firebase / coded errors
  if (err.code) return String(err.code);

  // Last resort
  return safeStringify(err) || tShared('An unexpected error occurred');
};

/** Recursively extract a human-readable string from common error shapes. */
const extractMessage = (value: unknown): string | null => {
  if (value == null) return null;
  if (typeof value === 'string') return value || null;

  if (Array.isArray(value)) {
    const joined = value
      .map((item) => extractMessage(item))
      .filter(Boolean)
      .join('; ');
    return joined || null;
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, any>;

    // Nested message/error/detail
    if (obj.message) return extractMessage(obj.message);
    if (obj.error) return extractMessage(obj.error);
    if (obj.detail) return extractMessage(obj.detail);

    // Field-level validation errors: { email: "required", name: "too short" }
    const entries = Object.entries(obj);
    if (entries.length) {
      const fields = entries
        .map(
          ([key, val]) =>
            `${key}: ${typeof val === 'string' ? val : safeStringify(val)}`,
        )
        .join(', ');
      return fields || null;
    }
  }

  return safeStringify(value) || null;
};

/** JSON.stringify that won't throw on circular refs. */
const safeStringify = (value: unknown): string | null => {
  try {
    const seen = new WeakSet();
    return JSON.stringify(value, (_key, val) => {
      if (typeof val === 'object' && val !== null) {
        if (seen.has(val)) return '[Circular]';
        seen.add(val);
      }
      return val;
    });
  } catch {
    return null;
  }
};

const trimTrailingZeros = (value: string) => {
  if (!value.includes('.')) {
    return normalizeNegativeZero(value);
  }

  const [integer, decimals] = value.split('.');
  const trimmedDecimals = decimals.replace(/0+$/, '');
  const result =
    trimmedDecimals.length > 0 ? `${integer}.${trimmedDecimals}` : integer;

  return normalizeNegativeZero(result);
};

const normalizeNegativeZero = (value: string) => {
  const numeric = Number(value);
  if (Object.is(numeric, -0) || numeric === 0) {
    return '0';
  }
  return value;
};

const toNonScientificString = (value: number) => {
  const direct = value.toString();
  if (!/e/i.test(direct)) {
    return direct;
  }

  return value.toLocaleString('fullwide', {
    useGrouping: false,
    maximumFractionDigits: 20,
  });
};

export const toFullPrecisionInventoryString = (value?: number | null) => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return '';
  }

  return trimTrailingZeros(toNonScientificString(value));
};
export const formatStock = (qty: number): string => {
  if (qty % 1 === 0) return qty.toString();
  // Show up to 3 decimal places, then strip trailing zeros
  const fixed3 = qty.toFixed(3);
  return fixed3.replace(/0+$/, '');
};
export const getInventoryDisplayParts = (value?: number | null) => {
  if (value === undefined || value === null || Number.isNaN(value)) {
    return {
      display: '',
      full: '',
      showPopover: false,
      isValueProvided: false,
    };
  }

  const display = value.toFixed(4);
  const full = toFullPrecisionInventoryString(value);
  const trimmedDisplay = trimTrailingZeros(display);

  return {
    display: trimmedDisplay,
    full,
    showPopover: trimmedDisplay !== full,
    isValueProvided: true,
  };
};

export const getPaymentTypeLabel = (type: PaymentMethodType): string => {
  // Literal keys so the i18n extractor keeps every payment-type label.
  switch (type) {
    case PaymentMethodType.CASH:
      return tShared('Cash');
    case PaymentMethodType.CARD:
      return tShared('Card');
    case PaymentMethodType.DEBT:
      return tShared('Debt');
    case PaymentMethodType.MOBILE_MONEY:
      return tShared('Mobile Money');
    case PaymentMethodType.CHEQUE:
      return tShared('Cheque');
    case PaymentMethodType.BANK_TRANSFER:
      return tShared('Bank Transfer');
    case PaymentMethodType.COUPON:
      return tShared('Coupon');
    case PaymentMethodType.LOYALTY_POINTS:
      return tShared('Loyalty Points');
    case PaymentMethodType.OTHER:
      return tShared('Other');
  }
  // Unknown values: remove the underscores and capitalize each word
  return String(type)
    .toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Format a platform payment type for display.
 * Returns the name only; linked TopupNumbers are shown separately.
 */
export const getPlatformPaymentTypeLabel = (pt: {
  name: string;
  payment_type: PaymentMethodType;
  useName?: boolean;
}): string => {
  if (pt.useName) {
    return pt.name;
  }
  return getPaymentTypeLabel(pt.payment_type);
};

/**
 * Derives a deterministic 6-digit PLU (1–999999) from a default barcode.
 * One-way, consistent: same barcode always produces the same PLU.
 */
export const barcodeToPLU = (barcode: string): number => {
  let hash = 2166136261; // FNV-1a offset basis
  for (let i = 0; i < barcode.length; i++) {
    hash ^= barcode.charCodeAt(i);
    hash = Math.imul(hash, 16777619); // FNV-1a prime
    hash = hash >>> 0; // unsigned 32-bit
  }
  return (hash % 999999) + 1; // 1–999999
};
